import React, { useMemo } from "react";

const DEFAULT_TERMS = ["referred by", "referred", "referrer", "referral", "ref"];

const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Renders `text` with case-insensitive matches of any `terms` wrapped
 * in a styled <mark>. Terms are matched on word boundaries so
 * "preferred", "referee", "reference" do NOT light up — only the
 * standalone referral-keyword variants do. Longer terms come first in
 * the regex so "referred by" matches before "referred".
 *
 * Helper UI for the office: highlights are visual hints only, not
 * interactive. Users still pick the source from the dropdown manually.
 */
const HighlightedText = ({ text, terms = DEFAULT_TERMS }) => {
  const segments = useMemo(() => {
    if (!text) return [];
    const sorted = [...terms]
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);
    if (sorted.length === 0) return [{ text, isMatch: false }];
    const pattern = new RegExp(
      `\\b(${sorted.map(escapeRegex).join("|")})\\b`,
      "gi"
    );
    const out = [];
    let last = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      if (m.index > last) {
        out.push({ text: text.slice(last, m.index), isMatch: false });
      }
      out.push({ text: m[0], isMatch: true });
      last = m.index + m[0].length;
      if (m[0].length === 0) pattern.lastIndex += 1;
    }
    if (last < text.length) {
      out.push({ text: text.slice(last), isMatch: false });
    }
    return out;
  }, [text, terms]);

  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {segments.map((seg, i) =>
        seg.isMatch ? (
          <mark
            key={i}
            style={{
              background: "#ffe97a",
              color: "#222",
              padding: "0 2px",
              borderRadius: 2,
            }}
          >
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </span>
  );
};

export default HighlightedText;
